import { useEffect, useRef } from "react";
import type { Locale } from "./i18n";
import { isSupportedPlatformURL, type PlatformID } from "./platforms";
import { localizedPath, type PageKind } from "./route";

export const PLATFORM_HANDOFF_STATE_KEY = "__grabsiloPlatformHandoff";
export const PLATFORM_HANDOFF_VERSION = 1;
export const PLATFORM_HANDOFF_TTL_MS = 60_000;
export const PLATFORM_HANDOFF_MAX_URL_LENGTH = 2048;

export interface PlatformHandoffPayload {
  version: typeof PLATFORM_HANDOFF_VERSION;
  target: PlatformID;
  url: string;
  createdAt: number;
}

const downloaderPages: Record<PlatformID, Exclude<PageKind, "not-found">> = {
  threads: "downloader",
  instagram: "instagram-downloader",
  youtube: "youtube-downloader",
  facebook: "facebook-downloader",
  tiktok: "tiktok-downloader",
  douyin: "douyin-downloader",
  x: "x-downloader",
  bilibili: "bilibili-downloader"
};

export function platformDownloaderPath(locale: Locale, platform: PlatformID): string {
  return localizedPath(locale, downloaderPages[platform]);
}

export function createPlatformHandoffState(
  existingState: unknown,
  target: PlatformID,
  input: string,
  createdAt = Date.now()
): Record<string, unknown> | null {
  const url = input.trim();
  if (!isValidHandoffURL(target, url) || !Number.isFinite(createdAt)) return null;
  const state = isStateRecord(existingState) ? { ...existingState } : {};
  state[PLATFORM_HANDOFF_STATE_KEY] = {
    version: PLATFORM_HANDOFF_VERSION,
    target,
    url,
    createdAt
  } satisfies PlatformHandoffPayload;
  return state;
}

export function readPlatformHandoff(
  state: unknown,
  expectedTarget: PlatformID,
  now = Date.now()
): PlatformHandoffPayload | null {
  if (!isStateRecord(state) || !Object.prototype.hasOwnProperty.call(state, PLATFORM_HANDOFF_STATE_KEY)) return null;
  const value = state[PLATFORM_HANDOFF_STATE_KEY];
  if (!isStateRecord(value)
    || value.version !== PLATFORM_HANDOFF_VERSION
    || value.target !== expectedTarget
    || typeof value.url !== "string"
    || value.url.length > PLATFORM_HANDOFF_MAX_URL_LENGTH
    || typeof value.createdAt !== "number"
    || !Number.isFinite(value.createdAt)
    || !Number.isFinite(now)) return null;
  const age = now - value.createdAt;
  const url = value.url.trim();
  if (age < 0 || age > PLATFORM_HANDOFF_TTL_MS || !isValidHandoffURL(expectedTarget, url)) return null;
  return { version: PLATFORM_HANDOFF_VERSION, target: expectedTarget, url, createdAt: value.createdAt };
}

export function clearPlatformHandoffState(state: unknown): unknown {
  if (!isStateRecord(state) || !Object.prototype.hasOwnProperty.call(state, PLATFORM_HANDOFF_STATE_KEY)) return state;
  const clean = { ...state };
  delete clean[PLATFORM_HANDOFF_STATE_KEY];
  return Object.keys(clean).length > 0 ? clean : null;
}

export function beginPlatformHandoff(locale: Locale, target: PlatformID, input: string): boolean {
  if (typeof window === "undefined") return false;
  const nextState = createPlatformHandoffState(window.history.state, target, input);
  if (!nextState) return false;
  const previousState = window.history.state;
  const previousURL = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  try {
    window.history.replaceState(nextState, "", platformDownloaderPath(locale, target));
    window.location.reload();
    return true;
  } catch {
    try {
      window.history.replaceState(previousState, "", previousURL);
    } catch {
      // The caller keeps the existing manual handoff link when History is unavailable.
    }
    return false;
  }
}

export function consumePlatformHandoff(expectedTarget: PlatformID, now = Date.now()): string | null {
  if (typeof window === "undefined") return null;
  const state = window.history.state;
  if (!isStateRecord(state) || !Object.prototype.hasOwnProperty.call(state, PLATFORM_HANDOFF_STATE_KEY)) return null;
  try {
    window.history.replaceState(clearPlatformHandoffState(state), "");
  } catch {
    return null;
  }
  return readPlatformHandoff(state, expectedTarget, now)?.url || null;
}

export function usePlatformHandoff(target: PlatformID, onHandoff: (url: string) => void | Promise<void>): void {
  const callbackRef = useRef(onHandoff);
  callbackRef.current = onHandoff;
  useEffect(() => {
    let active = true;
    const timer = window.setTimeout(() => {
      if (!active) return;
      const url = consumePlatformHandoff(target);
      if (url) void callbackRef.current(url);
    }, 0);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [target]);
}

function isValidHandoffURL(target: PlatformID, url: string): boolean {
  return url.length > 0
    && url.length <= PLATFORM_HANDOFF_MAX_URL_LENGTH
    && isSupportedPlatformURL(target, url);
}

function isStateRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
